import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

export interface SnapshotAnimationRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface VideoCropSnapshotAnimationProps {
    sourceRect: SnapshotAnimationRect;
    targetRect: SnapshotAnimationRect;
    imageSrc: string;
    onComplete: () => void;
}

const SNAP_DURATION_MS = 220;
const TOSS_DURATION_MS = 520;
const LAND_DURATION_MS = 150;

type Phase = 'snap' | 'toss' | 'land' | 'done';

export const VideoCropSnapshotAnimation: React.FC<
    VideoCropSnapshotAnimationProps
> = ({ sourceRect, targetRect, imageSrc, onComplete }) => {
    const prefersReducedMotion = useReducedMotion();
    const [phase, setPhase] = useState<Phase>('snap');

    useEffect(() => {
        if (prefersReducedMotion) {
            const timeoutId = window.setTimeout(() => {
                setPhase('done');
                onComplete();
            }, 50);
            return () => window.clearTimeout(timeoutId);
        }

        const snapTimeout = window.setTimeout(
            () => setPhase('toss'),
            SNAP_DURATION_MS,
        );
        const landTimeout = window.setTimeout(
            () => setPhase('land'),
            SNAP_DURATION_MS + TOSS_DURATION_MS,
        );
        const completeTimeout = window.setTimeout(
            () => {
                setPhase('done');
                onComplete();
            },
            SNAP_DURATION_MS + TOSS_DURATION_MS + LAND_DURATION_MS,
        );

        return () => {
            window.clearTimeout(snapTimeout);
            window.clearTimeout(landTimeout);
            window.clearTimeout(completeTimeout);
        };
    }, [onComplete, prefersReducedMotion]);

    if (phase === 'done') return null;

    const isSnap = phase === 'snap';
    const isLand = phase === 'land';

    const ghostStyle: React.CSSProperties = {
        position: 'fixed',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: 1000,
        willChange: 'transform, opacity, width, height',
    };

    const ghostAnimate = isSnap
        ? {
              x: sourceRect.left,
              y: sourceRect.top,
              width: sourceRect.width,
              height: sourceRect.height,
              opacity: 1,
              rotate: 0,
              scale: 1,
          }
        : isLand
          ? {
                x: targetRect.left,
                y: targetRect.top,
                width: targetRect.width,
                height: targetRect.height,
                opacity: 0,
                rotate: 0,
                scale: 1,
            }
          : {
                x: targetRect.left,
                y: targetRect.top,
                width: targetRect.width,
                height: targetRect.height,
                opacity: 1,
                rotate: -3,
                scale: 1,
            };

    const ghostTransition = isSnap
        ? { duration: SNAP_DURATION_MS / 1000, ease: 'easeOut' as const }
        : isLand
          ? { duration: LAND_DURATION_MS / 1000, ease: 'easeIn' as const }
          : {
                duration: TOSS_DURATION_MS / 1000,
                ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            };

    return createPortal(
        <div aria-hidden className="pointer-events-none">
            <AnimatePresence>
                {isSnap && !prefersReducedMotion && (
                    <motion.div
                        key="snap-flash"
                        className="fixed bg-white-100"
                        style={{
                            left: sourceRect.left,
                            top: sourceRect.top,
                            width: sourceRect.width,
                            height: sourceRect.height,
                            pointerEvents: 'none',
                            zIndex: 1001,
                            borderRadius: 2,
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0.85, 0] }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: SNAP_DURATION_MS / 1000,
                            ease: 'easeOut',
                            times: [0, 0.35, 1],
                        }}
                    />
                )}
            </AnimatePresence>

            <motion.img
                src={imageSrc}
                alt=""
                style={ghostStyle}
                initial={{
                    x: sourceRect.left,
                    y: sourceRect.top,
                    width: sourceRect.width,
                    height: sourceRect.height,
                    opacity: 1,
                    rotate: 0,
                    scale: 1,
                }}
                animate={ghostAnimate}
                transition={ghostTransition}
                className="rounded-[3px] object-cover shadow-[0_8px_28px_-4px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
                draggable={false}
            />
        </div>,
        document.body,
    );
};

VideoCropSnapshotAnimation.displayName = 'VideoCropSnapshotAnimation';
